/**
 * Journey context owner — Phase 2 §2.4 IMP-06.
 * Tracks in-flight journey id and JourneyShell context kind within a tenant.
 * OS/hardware back in-flow state lives in HierarchicalBackFlowContext (single publisher).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { JourneyId } from '../../registry/masterScreenRegistry.types.js';
import {
  createEmptyRouteContext,
  preserveContextsOnTransition,
  type RouteContext,
} from '../../navigation/navigationEngine.js';
import { useTenantCode } from '../tenant/TenantProvider.js';

export type JourneyContextKind =
  | 'POST_KIOSK_PRODUCT'
  | 'POST_KIOSK_DONATION'
  | 'PHONE_FIRST_PRODUCT'
  | 'PHONE_FIRST_DONATION'
  | null;

export interface JourneyContextValue {
  readonly journeyId: JourneyId | null;
  readonly contextKind: JourneyContextKind;
  readonly routeContext: RouteContext;
  readonly setJourney: (
    journeyId: JourneyId,
    contextKind: JourneyContextKind,
  ) => void;
  readonly clearJourney: () => void;
  readonly updateRouteContext: (patch: Partial<RouteContext>) => void;
}

const JourneyContext = createContext<JourneyContextValue | null>(null);

interface JourneyProviderProps {
  readonly children: ReactNode;
}

export function JourneyProvider({ children }: JourneyProviderProps): JSX.Element {
  const tenantCode = useTenantCode();
  const [journeyId, setJourneyId] = useState<JourneyId | null>(null);
  const [contextKind, setContextKind] = useState<JourneyContextKind>(null);
  const [routeContext, setRouteContext] = useState<RouteContext>(() =>
    createEmptyRouteContext(tenantCode),
  );

  const setJourney = useCallback(
    (nextJourneyId: JourneyId, nextContextKind: JourneyContextKind): void => {
      setJourneyId(nextJourneyId);
      setContextKind(nextContextKind);
      setRouteContext((previous) =>
        preserveContextsOnTransition(previous, {
          tenantCode,
          journeyId: nextJourneyId,
        }),
      );
    },
    [tenantCode],
  );

  const clearJourney = useCallback((): void => {
    setJourneyId(null);
    setContextKind(null);
    setRouteContext((previous) =>
      preserveContextsOnTransition(previous, {
        tenantCode,
        journeyId: null,
        checkoutSessionId: null,
        paymentActive: false,
        pickupToken: null,
        receiptRef: null,
      }),
    );
  }, [tenantCode]);

  const updateRouteContext = useCallback(
    (patch: Partial<RouteContext>): void => {
      setRouteContext((previous) => preserveContextsOnTransition(previous, patch));
    },
    [],
  );

  const value = useMemo<JourneyContextValue>(
    () => ({
      journeyId,
      contextKind,
      routeContext,
      setJourney,
      clearJourney,
      updateRouteContext,
    }),
    [
      journeyId,
      contextKind,
      routeContext,
      setJourney,
      clearJourney,
      updateRouteContext,
    ],
  );

  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
}

export function useJourneyContext(): JourneyContextValue {
  const ctx = useContext(JourneyContext);
  if (ctx === null) {
    throw new Error('useJourneyContext must be used inside <JourneyProvider>');
  }
  return ctx;
}

/** Safe for chrome that may mount outside JourneyProvider (platform `/shops`). */
export function useOptionalJourneyContext(): JourneyContextValue | null {
  return useContext(JourneyContext);
}
